import {
  Typography,
  Box,
  Button,
  Grid,
  IconButton,
  Divider,
} from '@mui/material';
import PaymentSummaryBoxComponent from './PaymentSummaryBoxComponent';
import {
  ArrowLeftIcon,
  DownloadCloudIcon,
} from '../../../../components/shared/CustomIcons';
import { PaymentStatus } from '../../../../hooks/queries/usePaymentList';
import { IPaymentDetails } from 'hooks/queries/usePaymentDetails';
import { formatDateInDDMMYY } from '../../../../utils/index';
import {
  getStatusColor,
  getStatusHoverColor,
  formatStatus,
  getPaymentSummaryHeader,
} from '../utils';

interface IPaymentDetailsComponentProps {
  payment: IPaymentDetails;
  handleBackClick: () => void;
  handleDownloadInvoice: () => void;
  handleReceiptDownload: () => void;
}

// currency is added as static until we'll get it from the backend
const currency = '£';

const PaymentDetailsComponent = ({
  payment,
  handleBackClick,
  handleDownloadInvoice,
  handleReceiptDownload,
}: IPaymentDetailsComponentProps) => {
  const {
    amount,
    description,
    date_of_payment: paymentDate,
    status,
    payment_method: paymentMethod,
  } = payment;

  const costBreakdown = [
    {
      label: description,
      amount: amount,
    },
  ];

  const isReceiptDownloadDisabled =
    status === PaymentStatus.Unpaid || !paymentDate;

  return (
    <Box sx={{ bgcolor: 'grey.50', px: 2, py: 8 }} className="flex-1">
      <Box sx={{ px: 1, mb: 2 }} className="d-flex align-items-center">
        <IconButton aria-label="back" sx={{ p: 0 }} onClick={handleBackClick}>
          <ArrowLeftIcon sx={{ fontSize: 40, color: 'text.primary' }} />
        </IconButton>
      </Box>
      <Box sx={{ gap: 1.5 }} className="d-flex align-items-center flex-1">
        <Typography variant="h5" sx={{ pl: 1, color: 'grey.900', pb: 4 }}>
          Payment details
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        mb={3}
        sx={{ flexWrap: 'nowrap' }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" ml={1}>
            Amount
          </Typography>

          <Typography variant="h6" mt={2} ml={1}>
            {currency}
            {amount}
          </Typography>
        </Box>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: 'solitude.main', mx: 2, mt: 3 }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1">Status</Typography>
          <Box
            sx={{
              backgroundColor: getStatusColor(status),
              px: 3,
              py: 1.3,
              borderRadius: 5,
              textAlign: 'center',
              marginTop: 1,
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: getStatusHoverColor(status),
              },
            }}
          >
            {formatStatus(status)}
          </Box>
        </Box>
      </Box>

      <PaymentSummaryBoxComponent
        totalAmount={amount}
        items={costBreakdown}
        currency="£"
        headerText={getPaymentSummaryHeader(status)}
        width="100%"
      />

      <Grid container spacing={2} mb={3} mt={1}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Lexend',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '20px',
                letterSpacing: '-0.75%',
              }}
            >
              Your payment method
            </Typography>
            <Typography variant="body1">{paymentMethod || '-'}</Typography>
          </Box>

          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2">Date of payment </Typography>
            <Typography variant="body1">
              {paymentDate ? formatDateInDDMMYY(new Date(paymentDate)) : '-'}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between">
        <Box>
          <Button
            disabled={!payment?.invoice_id}
            onClick={handleDownloadInvoice}
            sx={{
              textDecoration: 'none',
              cursor: !payment?.invoice_id ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '40px',
              backgroundColor: 'white',
              px: 1.2,
              py: 3,
            }}
          >
            Download invoice
            <Box
              sx={{
                width: 48,
                height: 48,
                backgroundColor: payment?.invoice_id ? '#6063F2' : '#B3B5F5',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                cursor: payment?.invoice_id ? 'pointer' : 'not-allowed',
                mt: 2,
              }}
            >
              <DownloadCloudIcon
                sx={{
                  color: payment?.invoice_id ? 'white' : 'grey.600',
                }}
              />
            </Box>
          </Button>
        </Box>
        <Box>
          <Button
            disabled={isReceiptDownloadDisabled}
            onClick={handleReceiptDownload}
            sx={{
              textDecoration: 'none',
              cursor: isReceiptDownloadDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '40px',
              backgroundColor: 'white',
              px: 1.2,
              py: 3,
            }}
          >
            Download receipt
            <Box
              sx={{
                width: 48,
                height: 48,
                backgroundColor: isReceiptDownloadDisabled
                  ? '#B3B5F5'
                  : '#6063F2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 'auto',
                cursor: isReceiptDownloadDisabled ? 'not-allowed' : 'pointer',
                mt: 2,
              }}
            >
              <DownloadCloudIcon
                sx={{
                  color: isReceiptDownloadDisabled ? 'grey.600' : 'white',
                }}
              />
            </Box>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentDetailsComponent;
